/**
 * Thin typed fetch client (DEVELOPMENT_WORKFLOW §2.6). The generated
 * OpenAPI types land in src/generated/ once the API serves a spec; until
 * then this provides the transport every consumer (web app, partners)
 * shares: auth header injection, envelopes, and the standard error shape.
 */

export interface ApiErrorBody {
  error: {
    code: string
    message: string
    details?: unknown[]
    requestId?: string
  }
}

export class ApiError extends Error {
  readonly code: string
  readonly status: number
  readonly details: unknown[] | undefined
  readonly requestId: string | undefined

  constructor(status: number, body: ApiErrorBody['error']) {
    super(body.message)
    this.name = 'ApiError'
    this.status = status
    this.code = body.code
    this.details = body.details
    this.requestId = body.requestId
  }
}

export interface ClientOptions {
  baseUrl: string
  /** Access-token supplier — the web app injects its in-memory JWT here. */
  getToken?: () => string | undefined
  /** API-key auth for machine callers. */
  apiKey?: string
  fetchImpl?: typeof fetch
  /** Browser clients use include for the cookie-bound refresh endpoint. */
  credentials?: RequestCredentials
  /** Called once after a 401; returning true retries with a fresh token. */
  onUnauthorized?: () => Promise<boolean>
  /** Observability hook (for example the web app's DB-outage banner). */
  onResponse?: (response: Response) => void | Promise<void>
}

export interface RequestOptions {
  query?: Record<string, string | number | boolean | undefined>
  body?: unknown
  idempotencyKey?: string
  ifMatchVersion?: number
  signal?: AbortSignal
}

export function createClient(options: ClientOptions) {
  const fetchImpl = options.fetchImpl ?? fetch

  async function raw(path: string, init: RequestInit = {}): Promise<Response> {
    const url = new URL(path, options.baseUrl)
    const send = async (): Promise<Response> => {
      const headers = new Headers(init.headers)
      if (!headers.has('Accept')) headers.set('Accept', 'application/json')
      const token = options.getToken?.()
      if (token && !headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`)
      if (options.apiKey && !headers.has('X-Api-Key')) headers.set('X-Api-Key', options.apiKey)
      return fetchImpl(url.toString(), {
        ...init,
        headers,
        ...(options.credentials && init.credentials === undefined ? { credentials: options.credentials } : {}),
      })
    }

    let response = await send()
    if (response.status === 401 && options.onUnauthorized && await options.onUnauthorized()) {
      response = await send()
    }
    await options.onResponse?.(response)
    return response
  }

  async function request<T>(
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    path: string,
    req: RequestOptions = {},
  ): Promise<T> {
    const url = new URL(path.startsWith('/') ? path : `/${path}`, options.baseUrl)
    if (req.query) {
      for (const [key, value] of Object.entries(req.query)) {
        if (value !== undefined) url.searchParams.set(key, String(value))
      }
    }

    const headers: Record<string, string> = { Accept: 'application/json' }
    if (req.body !== undefined) headers['Content-Type'] = 'application/json'
    if (req.idempotencyKey) headers['Idempotency-Key'] = req.idempotencyKey
    if (req.ifMatchVersion !== undefined) headers['If-Match'] = String(req.ifMatchVersion)

    const res = await raw(url.toString(), {
      method,
      headers,
      ...(req.body !== undefined ? { body: JSON.stringify(req.body) } : {}),
      ...(req.signal ? { signal: req.signal } : {}),
    })

    if (res.status === 204) return undefined as T

    const json = (await res.json().catch(() => undefined)) as unknown
    if (!res.ok) {
      const errBody = (json as ApiErrorBody | undefined)?.error ?? {
        code: 'INTERNAL',
        message: `Request failed with status ${res.status}`,
      }
      throw new ApiError(res.status, errBody)
    }
    return json as T
  }

  return {
    get: <T>(path: string, req?: RequestOptions) => request<T>('GET', path, req),
    post: <T>(path: string, req?: RequestOptions) => request<T>('POST', path, req),
    put: <T>(path: string, req?: RequestOptions) => request<T>('PUT', path, req),
    patch: <T>(path: string, req?: RequestOptions) => request<T>('PATCH', path, req),
    delete: <T>(path: string, req?: RequestOptions) => request<T>('DELETE', path, req),
    raw,
  }
}

export type Client = ReturnType<typeof createClient>
